import React, { useState, useEffect } from 'react';
import { LabService } from '../services/labService';

interface ChampionTransferModalProps {
    labId: string;
    labName: string;
    onTransfer: (championId: string) => Promise<void>;
    onCancel: () => void;
    isLoading: boolean;
}

interface EligibleChampion {
    id: string;
    email: string;
}

export const ChampionTransferModal: React.FC<ChampionTransferModalProps> = ({ labId, labName, onTransfer, onCancel, isLoading }) => {
    const [eligibleChampions, setEligibleChampions] = useState<EligibleChampion[]>([]);
    const [selectedChampion, setSelectedChampion] = useState<string>('');
    const [loadingChampions, setLoadingChampions] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        const fetchEligibleChampions = async () => {
            try {
              setLoadingChampions(true);
              const data = await LabService.getEligibleChampions(labId);
              setEligibleChampions(data.eligible_champions);
              setError('');
            } catch (err: unknown) {
              const message = err instanceof Error ? err.message : "Failed to load eligible champions.";
              setError(message);
              console.error('Error fetching eligible champions:', err);
            } finally {
              setLoadingChampions(false);
            }
        };

        fetchEligibleChampions();
    }, [labId]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        if (selectedChampion) {
            onTransfer(selectedChampion);
        }
    };

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-md">
                <h3 className="font-bold text-lg mb-4">Transfer Champion Status</h3>

                <div className="mb-4">
                    <p className="text-sm text-base-content/70 mb-2">
                        You are about to transfer your champion status for <strong>{labName}</strong> and leave the lab.
                    </p>
                    <p className="text-sm text-base-content/70">
                        Please select a lab member to become the new champion:
                    </p>
                </div>

                {error && (
                    <div className="alert alert-error mb-4">
                        <span>{error}</span>
                    </div>
                )}

                {loadingChampions ? (
                    <div className="flex justify-center py-8">
                        <span className="loading loading-spinner loading-md"></span>
                    </div>
                ) : eligibleChampions.length === 0 ? (
                        <div className="alert bg-amber-50 mb-4 border-2 border-solid border-[#eaddc9] rounded-lg shadow-md">
                        <span>No other lab members available to transfer champion status to. You must have at least one other lab member to transfer champion status.</span>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="form-control mb-6">
                            <label className="label">
                                <span className="label-text">Select New Champion</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={selectedChampion}
                                onChange={(e) => setSelectedChampion(e.target.value)}
                            >
                                <option value="">Choose a lab member...</option>
                                {eligibleChampions.map(champion => (
                                    <option key={champion.id} value={champion.id}>
                                        {champion.email}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="modal-action">
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={!selectedChampion || isLoading}
                            >
                                {isLoading ? (
                                    <span className="loading loading-spinner loading-xs"></span>
                                ) : (
                                    'Transfer & Leave'
                                )}
                            </button>
                            <button
                                type="button"
                                className="btn"
                                onClick={onCancel}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}

                {eligibleChampions.length === 0 && !loadingChampions && (
                    <div className="modal-action">
                        <button className="btn" onClick={onCancel} disabled={isLoading}>Close</button>
                    </div>
                )}
            </div>
        </div>
    );
};
